import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormNumberMultiselect } from "@/components/companies/shared/autosave-form/AutosaveFormNumberMultiselect";
import { AutosaveFormNumberSelect } from "@/components/companies/shared/autosave-form/AutosaveFormNumberSelect";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";

import { useSalesOptionsWithSelected } from "@/contexts/TaggableUsersContext";
import { useUpdateSetCompanyIntake } from "@/hooks/updateSets/useUpdateSetCompanyIntake";
import { nullableNumberSchema } from "@/lib/schemas";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CompanyIntake } from "@/types";

const salesTeamSchema = z.object({
  details: z.string().nullable(),
  dealTeam: z.array(z.coerce.number()),
  salesOwner: nullableNumberSchema,
});

export const SalesTeam = ({
  companyIntake,
  isEditing,
}: {
  companyIntake: CompanyIntake;
  isEditing: boolean;
}) => {
  const { mutate: updateCompanyIntake, isError } = useUpdateSetCompanyIntake();

  const form = useForm<z.infer<typeof salesTeamSchema>>({
    resolver: zodResolver(salesTeamSchema),
    defaultValues: companyIntake.sales,
  });

  const selectedSalesOwner = form.watch("salesOwner");
  const selectedDealTeam = form.watch("dealTeam");
  const salesOptionsForSelect = useSalesOptionsWithSelected([
    selectedSalesOwner,
    ...selectedDealTeam,
  ]);

  const handleSave = (data: z.infer<typeof salesTeamSchema>) => {
    mixpanelUtils.trackFormSubmission("Sales team", MIXPANEL_SCREENS.COMPANY);
    updateCompanyIntake({
      updateCompanyIntakeRequest: {
        sales: {
          details: data.details,
          dealTeam: data.dealTeam,
          salesOwner: data.salesOwner ?? null,
        },
      },
    });
  };

  const saveOnBlur = () => {
    void form.handleSubmit(handleSave)();
  };

  const dealTeamLabel = salesOptionsForSelect
    .filter((o) => companyIntake.sales.dealTeam.includes(o.value))
    .map((o) => o.label)
    .join(", ");
  const salesOwnerLabel =
    salesOptionsForSelect.find(
      (o) => o.value === companyIntake.sales.salesOwner,
    )?.label ?? null;

  const render = () => (
    <Column className="gap-6">
      {isEditing ? (
        <IntakeForm form={form} onSave={saveOnBlur}>
          <Column className="gap-6">
            <Row className="gap-6">
              <AutosaveFormNumberSelect
                className="flex-1"
                fieldName="salesOwner"
                form={form}
                label="Account Owner"
                onSelect={saveOnBlur}
                options={salesOptionsForSelect}
              />

              <AutosaveFormNumberMultiselect
                className="flex-1"
                fieldName="dealTeam"
                form={form}
                label="Deal Team"
                onSelect={saveOnBlur}
                options={salesOptionsForSelect}
              />
            </Row>

            <AutosaveFormRichTextInput
              editorHeight="150px"
              fieldName="details"
              form={form}
              infoTooltip={
                "Who conducted the initial tour or meeting?\nWho built the relationship with this customer?\nHow was the deal closed and any key details about the sales process?"
              }
              label="Details"
              onBlur={saveOnBlur}
            />

            {isError && (
              <ErrorText errorMessage="An error occurred while saving. Please try again." />
            )}
          </Column>
        </IntakeForm>
      ) : (
        <Column className="gap-4">
          <Row className="gap-6">
            <ReadOnlyField
              className="flex-1"
              label="Account Owner"
              value={salesOwnerLabel}
            />
            <ReadOnlyField
              className="flex-1"
              label="Deal Team"
              value={dealTeamLabel}
            />
          </Row>
          <ReadOnlyRichTextField
            label="Details"
            value={companyIntake.sales.details}
          />
        </Column>
      )}
    </Column>
  );

  return render();
};
